import React, { useContext } from "react";
import { Table } from "react-bootstrap";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import checkRoles from "@beeldit/user-and-access/auth/services/checkRoles";
import useDownloadPDFAction from "@bloonde/billing/invoices/actions/download-pdf/DownloadPDFAction";
import useMarkInvoicePaidAction from "../actions/mark-paid/useMarkInvoicePaidAction";

interface Props {
  data: {
    elements: any[];
    totalElements: number;
  };
  orderBy?: any;
  onOrderByChange?: any;
  refresh?: any;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Cálculos por línea de balance.
 * - El % de IVA se deduce de total_price / net_price (la combinación no se carga en
 *   el listado de balances).
 * - A pagar al proveedor = total con IVA − comisión con IVA. Asume que el proveedor
 *   emite la factura de venta al cliente (gestiona él el IVA de la venta).
 */
function lineFigures(el: any) {
  const total = parseFloat(el.concept?.total_price || 0);
  const net = parseFloat(el.concept?.net_price || 0);
  const comisionSinIva = parseFloat(el.concept?.commission_amount || 0);
  const taxRate = net > 0 ? total / net - 1 : 0;
  const ivaComision = round2(comisionSinIva * taxRate);
  const comisionConIva = round2(comisionSinIva + ivaComision);
  const aPagar = round2(total - comisionConIva);
  return { total, comisionSinIva, ivaComision, comisionConIva, aPagar };
}

function BalanceSummaryTable({ data, refresh }: Props) {
  const { downloadPDFFunction } = useDownloadPDFAction();
  const { markPaid } = useMarkInvoicePaidAction(refresh);
  const { user } = useContext(AuthContext);
  // El botón "Marcar pagada" es solo para admin (el endpoint también lo exige).
  // El badge de estado sí se muestra a todos (el productor ve si está pagada).
  const isAdmin = checkRoles(user, ["admin", "tenant_admin"]);

  const grouped: Record<string, any[]> = data.elements.reduce((acc, el) => {
    const invoiceId = el.invoice_line?.invoice_id;
    // Facturados: agrupar por factura (una factura es siempre de un único productor).
    // Pendientes (sin factura): agrupar por PRODUCTOR (emitter_id), no todos juntos.
    const groupKey = invoiceId
      ? `invoice:${invoiceId}`
      : `producer:${el.emitter_id ?? el.emitter?.id ?? "desconocido"}`;
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(el);
    return acc;
  }, {} as Record<string, any[]>);

  const orderedGroups = Object.entries(grouped).sort(([a], [b]) => {
    const aInvoice = a.startsWith("invoice:");
    const bInvoice = b.startsWith("invoice:");
    // Pendientes (agrupados por productor) primero, luego las facturas.
    if (aInvoice !== bInvoice) return aInvoice ? 1 : -1;
    return Number(a.split(":")[1]) - Number(b.split(":")[1]);
  });

  const globalTotals = data.elements.reduce(
    (acc, el) => {
      const f = lineFigures(el);
      acc.total += f.total;
      acc.comisionSinIva += f.comisionSinIva;
      acc.aPagar += f.aPagar;
      return acc;
    },
    { total: 0, comisionSinIva: 0, aPagar: 0 }
  );

  return (
    <>
      {/* Totales globales arriba a la derecha */}
      <div className="d-flex justify-content-end gap-5 fw-bold mb-3">
        <div>Total venta global: {formatCurrency(globalTotals.total)}</div>
        <div>Comisión global: {formatCurrency(globalTotals.comisionSinIva)}</div>
        <div>A pagar a proveedores: {formatCurrency(globalTotals.aPagar)}</div>
      </div>

      {/* Tabla */}
      <Table bordered hover size="sm" responsive>
        <thead>
          <tr className="table-primary">
            <th>Productor / Pedido</th>
            <th>Factura</th>
            <th>Fecha</th>
            <th title="Precio de venta con IVA del producto (lo que paga el cliente).">
              Total <small className="fw-normal text-muted">(con IVA)</small>
            </th>
            <th title="Comisión del marketplace SIN IVA = precio de venta sin IVA − importe del productor (net_price − producer_amount). Entre paréntesis, su IVA.">
              Comisión <small className="fw-normal text-muted">(sin IVA + IVA)</small>
            </th>
            <th title="Lo que el marketplace transfiere al proveedor = total con IVA − comisión con IVA. Asume que el proveedor emite la factura de venta al cliente.">
              A pagar proveedor
            </th>
          </tr>
        </thead>
        <tbody>
          {orderedGroups.map(([groupKey, group]) => {
            const invoice = group[0]?.invoice_line?.invoice;
            const facturaReference = invoice?.reference ?? "";
            const pdfUrl = invoice?.pdf?.[0]?.url;

            const agg = group.reduce(
              (acc, el) => {
                const f = lineFigures(el);
                acc.total += f.total;
                acc.comisionSinIva += f.comisionSinIva;
                acc.ivaComision += f.ivaComision;
                acc.aPagar += f.aPagar;
                return acc;
              },
              { total: 0, comisionSinIva: 0, ivaComision: 0, aPagar: 0 }
            );

            return (
              <React.Fragment key={groupKey}>
                <tr className="table-secondary fw-bold">
                  <td>{group[0]?.emitter?.user?.name ?? "Desconocido"}</td>
                  <td>
                    {facturaReference && pdfUrl ? (
                      <button
                        className="btn btn-link p-0 text-decoration-underline"
                        onClick={() => downloadPDFFunction(pdfUrl)}
                      >
                        {facturaReference}
                      </button>
                    ) : (
                      facturaReference
                    )}
                    {statusBadge(invoice)}
                    {isAdmin && invoice && invoice.status !== 2 && (
                      <button
                        className="btn btn-sm btn-outline-success ms-2 py-0"
                        onClick={() => markPaid(invoice.id)}
                      >
                        Marcar pagada
                      </button>
                    )}
                  </td>
                  <td>{formatDate(invoice?.date ?? "")}</td>
                  <td>{formatCurrency(agg.total)}</td>
                  <td>{commissionWithIva(agg.comisionSinIva, agg.ivaComision)}</td>
                  <td>{formatCurrency(agg.aPagar)}</td>
                </tr>
                {group.map((el) => {
                  const f = lineFigures(el);
                  return (
                    <tr key={el.id}>
                      <td>
                        {el.concept?.order?.reference && el.concept?.order?.id ? (
                          <a
                            href={`/panel/1/1/producer/order/${el.concept.order.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {el.concept.order.reference}
                          </a>
                        ) : (
                          el.concept?.order?.reference ?? ""
                        )}
                      </td>
                      <td></td>
                      <td>{formatDate(el.created_at)}</td>
                      <td>{formatCurrency(f.total)}</td>
                      <td>{commissionWithIva(f.comisionSinIva, f.ivaComision)}</td>
                      <td>{formatCurrency(f.aPagar)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

function statusBadge(invoice: any) {
  if (!invoice) return null;
  const status = invoice.status;
  const name = invoice.status_name ?? "";
  const cls =
    status === 2 ? "bg-success" : status === 3 ? "bg-danger" : "bg-warning text-dark";
  return <span className={`badge ${cls} ms-2`}>{name}</span>;
}

function commissionWithIva(comisionSinIva: number, ivaComision: number) {
  return (
    <>
      {formatCurrency(comisionSinIva)}{" "}
      <small className="text-muted">(+{formatCurrency(ivaComision)} IVA)</small>
    </>
  );
}

function formatCurrency(value: any): string {
  const num = typeof value === "string" ? parseFloat(value) : Number(value);
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(num || 0);
}

function formatDate(dateString: string): string {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return dateString;
  }
}

export default BalanceSummaryTable;
